import { useEffect, useMemo, useRef, useState } from "react";

// ECHO 발음기호 '기호 테스트'(2026-10-20 대표: 발음기호 못 읽는 초등 고학년 ~ 중고등, 무한 복습).
// question 모양은 api/assignment/ipa_course.py 가 만든다: 문항 창고(pool)에서 한 판에 quiz_item_count 개만 뽑는다.
// 한 판을 끝내면 새로 뽑아 다시 풀 수 있고, 틀린 문항 → 안 본 문항 → 나머지 차례로 뽑는다.
//
// 저장(ebook_state): item_stats {문항 id: {right, wrong, last}}, rounds [{right, total}], 첫 판 결과 results {문항 id: {score}}.
// 첫 판을 끝내면 완료이고 점수도 첫 판이다.

const PROMPT_TEXT_BY_TYPE = {
  choose_symbol: "이 낱말에 들어 있는 소리의 기호는?",
  pair: "들리는 대로 바르게 적은 발음기호는?",
  fill: "빈칸에 들어갈 기호는?",
  read: "이 발음기호는 어떤 낱말일까요?",
  pseudo: "들리는 소리를 발음기호로 적으면?",
};
// 한 판에 섞을 꼴과 수(창고에 모자라면 다른 꼴로 채운다)
const ROUND_MIX = [
  ["choose_symbol", 3],
  ["pair", 2],
  ["fill", 2],
  ["read", 3],
  ["pseudo", 2],
];
// 소리를 먼저 들려주는 꼴. read 는 기호만 보고 골라야 해서 답한 뒤에 들려준다
const AUTO_PLAY_TYPE_LIST = ["choose_symbol", "pair", "pseudo"];

const shuffled = (list) => {
  const copy = [...list];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const other = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[other]] = [copy[other], copy[index]];
  }
  return copy;
};

// 틀린 적이 있고 아직 못 고친 것 → 안 본 것 → 나머지. 같은 무리 안은 섞는다
const pickRound = (pool, itemStats, count) => {
  const rankOf = (item) => {
    const stat = itemStats[item.id];
    if (!stat) return 1;
    if (stat.last === "wrong") return 0;
    return 2;
  };
  const ranked = (list) => [0, 1, 2].flatMap((rank) => shuffled(list.filter((item) => rankOf(item) === rank)));
  const picked = [];
  ROUND_MIX.forEach(([type, typeCount]) => {
    picked.push(...ranked(pool.filter((item) => item.type === type)).slice(0, typeCount));
  });
  if (picked.length < count) {
    picked.push(...ranked(pool.filter((item) => !picked.includes(item))).slice(0, count - picked.length));
  }
  return shuffled(picked.slice(0, count));
};

const IpaQuizStep = ({ question, savedState, isLocked, save, onFinish }) => {
  const pool = useMemo(() => question.pool || [], [question.pool]);
  const itemCount = Math.min(question.quiz_item_count || 12, pool.length);
  const audioRef = useRef(null);
  const [itemStats, setItemStats] = useState(() => savedState.item_stats || {});
  const [roundList, setRoundList] = useState(() => savedState.rounds || []);
  const [round, setRound] = useState(() => pickRound(pool, savedState.item_stats || {}, itemCount));
  const [itemIndex, setItemIndex] = useState(0);
  const [pickedByIndex, setPickedByIndex] = useState({});
  const [isSummary, setIsSummary] = useState(false);
  const item = round[itemIndex] || {};
  const picked = pickedByIndex[itemIndex];
  const isAnswered = picked !== undefined;

  const play = (url) => {
    if (!url) return;
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.play().catch(() => {});
  };
  useEffect(() => () => audioRef.current && audioRef.current.pause(), []);

  useEffect(() => {
    if (!isSummary && AUTO_PLAY_TYPE_LIST.includes(item.type)) play(item.audio_url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIndex, round, isSummary]);

  const choose = (choice) => {
    if (isAnswered || isLocked) return;
    const isRight = choice === item.answer;
    setPickedByIndex({ ...pickedByIndex, [itemIndex]: choice });
    const previous = itemStats[item.id] || { right: 0, wrong: 0 };
    const nextStats = {
      ...itemStats,
      [item.id]: {
        right: previous.right + (isRight ? 1 : 0),
        wrong: previous.wrong + (isRight ? 0 : 1),
        last: isRight ? "right" : "wrong",
      },
    };
    setItemStats(nextStats);
    save({ item_stats: nextStats }, false);
    // 답을 고르면 낱말 소리를 들려준다(기호 → 소리를 이어 주려고)
    play(item.audio_url);
  };

  const finishRound = () => {
    const right = round.filter((one, index) => pickedByIndex[index] === one.answer).length;
    const nextRoundList = [...roundList, { right, total: round.length }];
    setRoundList(nextRoundList);
    // 점수는 첫 판만 센다(calculate_grade 가 ebook_state.results 의 score 를 센다). 다시 푸는 판은 복습이다
    const patch = { item_stats: itemStats, rounds: nextRoundList };
    if (!roundList.length && !savedState.results) {
      patch.results = Object.fromEntries(
        round.map((one, index) => [one.id, { score: pickedByIndex[index] === one.answer ? 1 : 0 }])
      );
    }
    save(patch, true);
    setIsSummary(true);
  };

  const startNewRound = () => {
    setRound(pickRound(pool, itemStats, itemCount));
    setItemIndex(0);
    setPickedByIndex({});
    setIsSummary(false);
  };

  if (!pool.length) {
    return <div className="ipa-quiz ipa-quiz--empty">문제를 준비하고 있어요.</div>;
  }

  if (isSummary) {
    const right = round.filter((one, index) => pickedByIndex[index] === one.answer).length;
    const wrongList = round.filter((one, index) => pickedByIndex[index] !== one.answer);
    const seenCount = pool.filter((one) => itemStats[one.id]).length;
    const fixedCount = pool.filter((one) => (itemStats[one.id] || {}).last === "right").length;
    return (
      <div className="ipa-quiz ipa-quiz--summary">
        <div className="ipa-quiz__score">
          <span>{roundList.length}번째 판</span>
          <b>
            {right} / {round.length}
          </b>
        </div>
        <div className="ipa-quiz__meter">
          <div>
            <span>푼 문제</span>
            <b>
              {seenCount} / {pool.length}
            </b>
          </div>
          <div>
            <span>맞힌 문제</span>
            <b>
              {fixedCount} / {pool.length}
            </b>
          </div>
        </div>
        {wrongList.length > 0 && (
          <div className="ipa-quiz__wrong-list">
            <div className="ipa-quiz__wrong-title">틀린 문제(누르면 소리)</div>
            {wrongList.map((one) => (
              <button type="button" key={one.id} className="ipa-quiz__wrong" onClick={() => play(one.audio_url)}>
                <span>{one.prompt || one.word || "소리"}</span>
                <b>{one.answer}</b>
              </button>
            ))}
          </div>
        )}
        <div className="phonics__footer">
          <span className="phonics__count">다시 풀면 틀린 문제와 새 문제가 먼저 나와요</span>
          <button type="button" className="phonics__next" onClick={startNewRound}>
            새 문제로 다시 풀기
          </button>
          <button type="button" className="phonics__finish" onClick={onFinish}>
            끝내기
          </button>
        </div>
      </div>
    );
  }

  const showsWord = ["choose_symbol", "pair"].includes(item.type) || (isAnswered && item.word && item.type !== "pseudo");
  return (
    <div className="ipa-quiz">
      <div className="phonics-quiz__progress">
        <span className="phonics-quiz__block">기호 테스트</span>
        <span>
          {itemIndex + 1} / {round.length}
        </span>
        <div className="phonics-quiz__bar">
          <div style={{ width: `${((itemIndex + 1) / Math.max(round.length, 1)) * 100}%` }} />
        </div>
      </div>
      <div className="ipa-quiz__card">
        <div className="ipa-quiz__ask">{PROMPT_TEXT_BY_TYPE[item.type] || "알맞은 것을 고르세요."}</div>
        {item.prompt && <div className="ipa-quiz__symbol">{item.prompt}</div>}
        {showsWord && <div className="ipa-quiz__word">{item.word}</div>}
        {item.type !== "read" || isAnswered ? (
          <button type="button" className="ipa-quiz__listen" onClick={() => play(item.audio_url)} disabled={!item.audio_url}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M4 9.5h3.5L12 6v12l-4.5-3.5H4z" fill="currentColor" />
              <path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            {item.type === "pseudo" ? "다시 듣기" : "듣기"}
          </button>
        ) : null}
      </div>
      <div className={`ipa-quiz__choices${(item.choices || []).length === 2 ? " is-two" : ""}`}>
        {(item.choices || []).map((choice) => {
          const stateClass = isAnswered
            ? choice === item.answer
              ? " is-right"
              : choice === picked
              ? " is-wrong"
              : " is-dim"
            : "";
          return (
            <button
              type="button"
              key={choice}
              className={`ipa-quiz__choice${item.type === "read" ? " is-word" : ""}${stateClass}`}
              onClick={() => choose(choice)}
            >
              {choice}
            </button>
          );
        })}
      </div>
      <div className="phonics__footer">
        <span className="phonics__count">
          {isAnswered ? (picked === item.answer ? "맞았어요!" : `정답은 ${item.answer}`) : "고르면 바로 알려 줘요"}
        </span>
        <button
          type="button"
          className="phonics__finish"
          disabled={!isAnswered}
          onClick={() => (itemIndex >= round.length - 1 ? finishRound() : setItemIndex(itemIndex + 1))}
        >
          {itemIndex >= round.length - 1 ? "결과 보기" : "다음"}
        </button>
      </div>
    </div>
  );
};

export default IpaQuizStep;
